/*
  Research Foundations — public page explaining the behavioural frameworks
  SugarSense draws on. Verifiable proof asset for users + buyer-due-diligence.

  Tone: plain English, two-line explainers, never moralising or clinical.
  Each card cites the *field* in calm prose, never a fabricated paper.
*/
import { Link } from "react-router-dom";
import { ArrowRight, Brain, Repeat, Heart, Scale, LineChart, Layers, Shield, BookOpen } from "lucide-react";
import { SeoHead } from "@/components/SeoHead";

const FRAMEWORKS = [
  {
    icon: Heart,
    name: "Attachment theory",
    sub: "How early relationships teach us to expect closeness, distance, or both.",
    body: (
      <>
        <p>
          Attachment theory describes how the first close bonds you experience — usually with parents
          or caregivers — quietly install a working model of what relationships are supposed to feel
          like. Some adults grow up expecting closeness to be safe and consistent ('secure'). Others
          learn that closeness is fragile and must be earned ('anxious'), or that closeness comes with
          a cost and is best kept at arm's length ('avoidant'). A fourth pattern ('disorganised') mixes
          both — wanting closeness but flinching from it.
        </p>
        <p>
          In adult dating, these patterns shape how partners read silence, distance, intensity, and
          repair. SugarSense uses attachment-theory research to read what a specific behaviour is
          <em> likely </em>signalling — without ever diagnosing the person. The framework's
          adult-attachment research base is well established and continually replicated.
        </p>
      </>
    ),
    tags: ["secure", "anxious", "avoidant", "disorganised"],
  },
  {
    icon: Repeat,
    name: "Intermittent reinforcement",
    sub: "Why unpredictable rewards keep you more invested than steady ones.",
    body: (
      <>
        <p>
          Behavioural-learning research is unambiguous: unpredictable rewards create stronger,
          stickier attachment than predictable ones. A partner whose warmth, attention, or plans come
          and go in waves is delivering a far more compulsive emotional schedule than a partner who
          shows up at consistent levels — even when the consistent partner is, by every reasonable
          measure, the better option.
        </p>
        <p>
          Naming this pattern stops you from interpreting your own gripped feeling as 'this must be
          real love'. Sometimes it isn't; sometimes it's just an extremely efficient reward schedule.
          SugarSense reads for it whenever the calendar, presence or affection has a marked rhythm
          of advance-and-retreat.
        </p>
      </>
    ),
    tags: ["reward schedule", "advance & retreat", "stickiness"],
  },
  {
    icon: Layers,
    name: "Schema therapy",
    sub: "The lifelong templates that decide who feels familiar and who feels safe.",
    body: (
      <>
        <p>
          Schema-therapy research identifies a set of long-standing emotional templates — sometimes
          called early maladaptive schemas — that quietly shape who feels familiar, who feels
          dangerous, and what we recognise as 'love'. Common ones include abandonment, defectiveness,
          emotional deprivation, mistrust, and self-sacrifice.
        </p>
        <p>
          Schemas explain why a dynamic that an outsider can clearly see is unkind can still feel like
          home to the person inside it. It also explains why someone secure can feel <em>boring</em> —
          the absence of the familiar emotional signal can register as 'no chemistry'. SugarSense
          draws on schema-therapy clinical work to name what is being mistaken for connection.
        </p>
      </>
    ),
    tags: ["abandonment", "defectiveness", "emotional deprivation", "self-sacrifice"],
  },
  {
    icon: Scale,
    name: "Social-exchange theory",
    sub: "Every relationship has an unspoken ledger of what's given and received.",
    body: (
      <>
        <p>
          Social-exchange theory is a long-running sociology and social-psychology framework. The
          short version: people stay in a relationship as long as the perceived rewards exceed the
          perceived costs <em>relative to the alternatives they believe they have</em>. The ledger
          isn't only money — it's time, attention, status, certainty, sexual energy, emotional
          labour.
        </p>
        <p>
          In premium and provider-style dating, the asymmetry of that ledger is the whole subject.
          SugarSense uses social-exchange thinking to ask quietly: what is each side actually
          exchanging, what is being assumed, and is the trade you're agreeing to one you'd consciously
          choose if it were written down on paper?
        </p>
      </>
    ),
    tags: ["costs vs rewards", "alternatives", "asymmetry"],
  },
  {
    icon: Shield,
    name: "Trauma-informed psychology",
    sub: "Why past experience changes which patterns feel safe today.",
    body: (
      <>
        <p>
          Trauma-informed psychology starts from a simple premise: behaviour that looks puzzling from
          the outside is usually intelligent from the inside, given what the person has lived through.
          A woman who tolerates inconsistency may have learned, very early, that asking for steadiness
          felt unsafe. A man who pulls away the moment intimacy lands may have learned that closeness
          eventually hurts.
        </p>
        <p>
          SugarSense uses a trauma-informed lens to read patterns with compassion rather than blame —
          on either side. The point is never to label anyone, but to help you see what your nervous
          system is responding to so you can choose more freely.
        </p>
      </>
    ),
    tags: ["safety cues", "nervous-system response", "compassionate reading"],
  },
  {
    icon: LineChart,
    name: "Behavioural economics",
    sub: "How we predictably mis-estimate the cost of staying — and the cost of leaving.",
    body: (
      <>
        <p>
          Behavioural-economics research catalogues the small, predictable ways human decision-making
          drifts from cool rationality. Sunk-cost thinking ('I've already invested so much'), loss
          aversion ('what if the next person is worse?'), present-bias ('the good weekend just
          happened, that <em>has</em> to count') — these aren't character flaws. They're built-in.
        </p>
        <p>
          They especially distort dating, where the emotional cost of leaving is paid up-front and the
          benefit of leaving is paid much later. SugarSense names which mental shortcut is most likely
          firing — so you can see your own decision a bit more clearly.
        </p>
      </>
    ),
    tags: ["sunk cost", "loss aversion", "present-bias"],
  },
];


export function ResearchFoundations() {
  return (
    <div data-testid="research-foundations" className="overflow-x-hidden">
      <SeoHead
        title="Research foundations — the behavioural frameworks SugarSense uses"
        description="The six well-established behavioural frameworks behind SugarSense's advice — attachment theory, intermittent reinforcement, schema therapy, social-exchange theory, trauma-informed psychology, and behavioural economics. Plain English, no jargon."
        path="/research"
      />

      {/* Hero */}
      <section className="ss-bg-radiant border-b border-champagne">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">The thinking behind the advice</p>
          <h1 className="mt-3 font-heading text-4xl sm:text-6xl text-cocoa leading-[1.05]">
            Six frameworks. <span className="italic brand-shine">One calm, well-grounded voice.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-cocoa/70 text-base sm:text-lg leading-relaxed">
            SugarSense doesn't invent its patterns. Every pattern in the library, and every piece of
            advice, rests on six well-established ways of understanding how people behave in
            relationships. None of them are clinical. None of them are about diagnosing the person
            you're with. They're about looking at what's going on calmly, in language that takes you seriously.
          </p>
        </div>
      </section>

      {/* Anchor strip */}
      <nav data-testid="research-anchor-strip" className="border-b border-champagne bg-blush/20 overflow-x-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex gap-3 sm:gap-5 text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-cocoa/55 whitespace-nowrap">
          {FRAMEWORKS.map((f) => (
            <a key={f.name} href={`#${slugify(f.name)}`} className="hover:text-cocoa transition-colors">{f.name}</a>
          ))}
        </div>
      </nav>

      {/* Frameworks */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-20 space-y-16">
        {FRAMEWORKS.map((f, i) => (
          <article key={f.name} id={slugify(f.name)} data-testid={`research-${slugify(f.name)}`} className="scroll-mt-24">
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-full bg-mauve/10 flex items-center justify-center">
                <f.icon className="text-mauve" size={20} strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <p className="text-[10px] tracking-[0.4em] uppercase text-mauve">Framework {String(i + 1).padStart(2, "0")}</p>
                <h2 className="mt-1 font-heading text-3xl sm:text-4xl text-cocoa leading-tight">{f.name}</h2>
                <p className="mt-2 font-heading italic text-lg text-cocoa/65">{f.sub}</p>
              </div>
            </div>
            <div className="mt-6 space-y-4 text-cocoa/80 leading-relaxed">{f.body}</div>
            {f.tags?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {f.tags.map((t) => (
                  <span key={t} className="text-[10px] tracking-widest uppercase text-mauve px-3 py-1 rounded-full bg-mauve/8 border border-mauve/15">{t}</span>
                ))}
              </div>
            )}
          </article>
        ))}
      </section>

      {/* What we never do */}
      <section className="border-t border-champagne bg-blush/15">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">A grown-up promise</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl text-cocoa leading-tight">What you'll never see here.</h2>
          <ul className="mt-7 space-y-4 text-cocoa/80 leading-relaxed">
            <li className="flex gap-3"><span className="font-heading text-mauve text-xl leading-none">·</span><span><strong>No clinical diagnoses.</strong> SugarSense will never call someone a narcissist, sociopath or any other clinical label. Behaviour is described, not pathologised.</span></li>
            <li className="flex gap-3"><span className="font-heading text-mauve text-xl leading-none">·</span><span><strong>No fabricated citations.</strong> When research is referenced, it's referenced as a <em>field</em> ('attachment-theory research', 'schema-therapy clinical work'). You'll never see invented paper titles or made-up statistics.</span></li>
            <li className="flex gap-3"><span className="font-heading text-mauve text-xl leading-none">·</span><span><strong>No moralising — about either of you.</strong> The aim is clarity, not judgement. Adults are allowed unusual arrangements; what matters is whether they're being honest with themselves.</span></li>
            <li className="flex gap-3"><span className="font-heading text-mauve text-xl leading-none">·</span><span><strong>No professional advice.</strong> Nothing here replaces a therapist, lawyer or financial adviser. SugarSense is a private place to think things through, not a substitute for a regulated profession.</span></li>
          </ul>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="border-t border-champagne">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Ready to see it work?</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl text-cocoa leading-tight">
            Start with one situation. Free.
          </h2>
          <p className="mt-4 text-cocoa/70 max-w-lg mx-auto leading-relaxed">
            Tell SugarSense what happened and see whether another perspective helps. Free to start.
            Private to you.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register"><button data-testid="research-cta-register" className="ss-pill ss-pill-primary h-12 px-7 w-full sm:w-auto">Try it free <ArrowRight size={16} /></button></Link>
            <Link to="/archetypes"><button className="ss-pill ss-pill-ghost h-12 w-full sm:w-auto"><BookOpen size={14} /> Browse relationship patterns</button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
